<template>
  <div class="sa-detail">
    <!-- TOP TOOLBAR -->
    <div class="sa-detail-toolbar">
      <router-link :to="backLocation" class="btn btn-outline-primary sa-detail-back font-weight-bold">
        <CIcon name="cil-arrow-left" class="mr-1" />
        {{ text('กลับหน้ารายการ', 'Back to requests') }}
      </router-link>
      <button
        type="button"
        class="btn btn-outline-secondary sa-refresh-action font-weight-bold"
        :disabled="loading"
        @click="loadTicket"
      >
        <CIcon name="cil-reload" :class="{ 'fa-spin': loading }" class="mr-1" />
        {{ text('รีเฟรชข้อมูล', 'Refresh') }}
      </button>
    </div>

    <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
    <p v-if="loading && !report" class="sa-loading" role="status">{{ text('กำลังโหลดรายละเอียด…', 'Loading details…') }}</p>

    <template v-if="report">
      <!-- HERO BANNER -->
      <section class="sa-detail-hero">
        <div>
          <p>{{ text('งานแจ้งซ่อม', 'Repair request') }}</p>
          <h1>{{ report.ticketNumber }}</h1>
          <span>{{ getUserName() }} • {{ getReporterAffiliation() }}</span>
        </div>
        <div class="sa-current-status">
          <strong :class="{ 'status-cancelled': report.status === 'cancelled' }">{{ statusLabel(report.status) }}</strong>
          <p>{{ nextStep }}</p>
          <small>{{ text('ข้อมูลล่าสุด', 'Updated') }} {{ formatDate(report.updatedAt || report.createdAt) }}</small>
        </div>
      </section>

      <!-- PANEL 1: REPAIR PROGRESS & STEP CONTENT -->
      <section class="sa-detail-panel">
        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 10px;">
          <h2 class="m-0">{{ text('ความคืบหน้าการซ่อม', 'Repair progress') }}</h2>
          <div>
            <button
              type="button"
              class="btn btn-sm font-weight-bold shadow-sm d-flex align-items-center"
              style="background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; border-radius: 8px; padding: 6px 14px; transition: all 0.2s ease-in-out;"
              @click="selectStep(activeStep === 'all' ? defaultDetailStep : 'all')"
            >
              <CIcon name="cil-list" size="sm" class="mr-1 text-primary" />
              {{ activeStep === 'all' ? text('ดูแยกตามขั้นตอน', 'View by step') : text('ดูข้อมูลทั้งหมด', 'View all') }}
            </button>
          </div>
        </div>

        <ol class="sa-timeline">
          <li
            v-for="(step, index) in timeline"
            :key="step.status"
            :class="{
              reached: step.reached,
              current: step.status === report.status,
              'active-step': activeStep === step.status,
              'disabled-step': !step.reached
            }"
            :aria-current="step.status === report.status ? 'step' : null"
          v-keyboard-activate role="button" :tabindex="step.reached ? 0 : -1" :aria-disabled="String(!step.reached)" :aria-pressed="String(activeStep === step.status)"
            @click="step.reached ? selectStep(step.status) : null"
            :title="step.reached ? text('คลิกเพื่อดูขั้นตอนนี้', 'Click to view step') : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached yet')"
          >
            <span class="sa-step-number">{{ index + 1 }}</span>
            <strong>{{ step.label }}</strong>
            <small>{{ step.date ? formatDate(step.date) : (step.reached ? (step.status === 'assigned' && report.status === 'pending' ? text('รอดำเนินการ', 'Pending') : text('ไม่มีเวลาบันทึก', 'Time not recorded')) : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached')) }}</small>
          </li>
        </ol>

        <p v-if="report.status === 'cancelled'" class="alert alert-secondary">
          <strong class="text-danger">{{ text('ยกเลิกงาน:', 'Cancelled:') }}</strong> {{ report.cancelReason || text('ไม่ได้ระบุเหตุผล', 'No reason recorded') }}
        </p>

        <!-- STEP CONTENT CONTAINER -->
        <div class="sa-step-container">
          <!-- Step 1: แจ้งเรื่อง -->
          <div v-show="(activeStep === 'pending' || activeStep === 'all') && isStepReached('pending')" class="sa-step-box">
            <div class="sa-step-box-header">
              <h3>
                <span class="badge badge-primary mr-1">1</span>
                {{ text('รายละเอียดการแจ้งซ่อม', 'Report details') }}
              </h3>
              <span class="text-muted small">{{ formatDate(getReportInformedDate()) }}</span>
            </div>

            <TicketDetailSummary
              :ticket-number="report.ticketNumber || '-'"
              :reporter="getUserName()"
              :affiliation="getReporterAffiliation()"
              :section="getSectionName()"
              :personal-phone="getPersonalPhone()"
              :office-phone="getOfficePhone()"
              :job-type="getCategoryName()"
              :work-area="getLocationFull()"
              :time-slot="getAvailableTime()"
              :on-behalf-of="getOnBehalfOf()"
              :technician-phone="getTechnicianPhone()"
            />
            <h4 class="mt-4 mb-2 font-weight-bold" style="font-size: 16px; color: #1e293b;">
              {{ text('รายละเอียดปัญหา', 'Problem description') }}
            </h4>
            <p class="sa-description" v-html="getProblemDetails()"></p>
            <p v-if="getAssetCode()" class="font-weight-bold text-danger mb-3">
              <CIcon name="cil-tag" class="mr-1" /> {{ text('รหัสครุภัณฑ์:', 'Asset code:') }} {{ getAssetCode() }}
            </p>

            <div v-if="isOutdoor()" class="mb-4">
              <label class="lbl font-weight-bold text-dark mb-2">{{ $t('ticketDetail.mapPoint') }}</label>
              <div class="map-container rounded overflow-hidden" style="height: 350px;">
                <div ref="mapContainer" style="width: 100%; height: 100%"></div>
              </div>
              <div class="mt-2 text-right">
                <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                  <CIcon name="cil-map" class="mr-1" /> {{ $t('ticketDetail.openMaps') }} ↗
                </a>
              </div>
            </div>

            <div class="mt-3">
              <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพปัญหาที่แจ้ง', 'Reported problem photo') }}</h5>
              <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" @open="openImage" @error="handleImageError" />
              <p v-else class="text-muted small">{{ text('ไม่มีภาพแนบ', 'No image attached') }}</p>
            </div>
          </div>

          <!-- Step 2: รับเรื่อง/มอบหมาย -->
          <div v-show="(activeStep === 'assigned' || activeStep === 'all') && isStepReached('assigned')" class="sa-step-box">
            <div class="sa-step-box-header">
              <h3>
                <span class="badge badge-primary mr-1">2</span>
                {{ text('การรับเรื่องและมอบหมายงาน', 'Assignment details') }}
              </h3>
              <span class="text-muted small" v-if="getSupervisorInformedDate()">
                {{ formatDate(getSupervisorInformedDate()) }}
              </span>
            </div>

            <!-- Postpone Active Notice inside Step 2 -->
            <div v-if="isPostponedActive" class="postpone-status-banner p-3 mb-3 rounded border" style="background: #fffbeb; border-color: #fde68a !important;">
              <div class="d-flex align-items-center">
                <span class="mr-2" style="font-size: 1.4rem;">⏸️</span>
                <div>
                  <strong class="text-dark d-block">
                    {{ text('สถานะงาน: ได้รับการเลื่อนซ่อม', 'Status: Repair Postponed') }}
                  </strong>
                  <span class="text-muted small">
                    {{ text('งานนี้ได้รับการเลื่อนซ่อม โดยจะเริ่มดำเนินการใหม่อีกครั้งในวันที่', 'This repair is postponed and will resume on') }}
                    <strong class="text-primary">{{ formatDate(report.postponedUntil) }}</strong>
                  </span>
                </div>
              </div>
            </div>

            <div class="sa-responsibility">
              <div>
                <span>{{ text('ช่างผู้รับผิดชอบ', 'Assigned technicians') }}</span>
                <strong>{{ getTechnicianName() || text('ยังไม่มอบหมาย', 'Unassigned') }}</strong>
              </div>
              <div>
                <span>{{ text('หน่วยงานรับผิดชอบ', 'Maintenance section') }}</span>
                <strong>{{ getSectionName() }}</strong>
              </div>
              <div>
                <span>{{ text('กำหนดซ่อม', 'Repair deadline') }}</span>
                <strong :class="{ 'text-danger': overdue }">
                  <template v-if="isPostponedActive">
                    {{ text('เลื่อนซ่อม (เริ่มนับ SLA: ', 'Postponed (SLA starts: ') }}{{ formatDate(report.postponedUntil) }})
                  </template>
                  <template v-else>
                    {{ report.slaDeadline ? formatDate(report.slaDeadline) : text('ยังไม่ระบุ', 'Not set') }}
                  </template>
                </strong>
                <small v-if="overdue" class="text-danger d-block mt-1">{{ text('เกินกำหนดซ่อม', 'Overdue') }}</small>
              </div>
              <div>
                <span>{{ text('ระยะเวลาซ่อมโดยประมาณ', 'Estimated repair time') }}</span>
                <strong>{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
              </div>
            </div>
          </div>

          <!-- Step 3: กำลังซ่อม -->
          <div v-show="(activeStep === 'in_progress' || activeStep === 'all') && isStepReached('in_progress')" class="sa-step-box">
            <div class="sa-step-box-header">
              <h3>
                <span class="badge badge-primary mr-1">3</span>
                {{ text('การดำเนินงานซ่อม', 'Repair in progress') }}
              </h3>
              <span class="text-muted small" v-if="getWorkStartedDate()">
                {{ formatDate(getWorkStartedDate()) }}
              </span>
            </div>

            <div class="p-3 rounded bg-light border mb-3">
              <div class="row">
                <div class="col-md-6 mb-2">
                  <span class="text-muted small d-block">{{ text('สถานะการดำเนินงาน', 'Progress status') }}</span>
                  <strong class="text-dark">{{ report.status === 'in_progress' ? text('ช่างกำลังดำเนินการซ่อมแซม', 'Technician is actively working on the repair') : (['done', 'welldone'].includes(report.status) ? text('ดำเนินการซ่อมเรียบร้อยแล้ว', 'Repair work completed') : text('รอช่างเริ่มดำเนินการ', 'Awaiting repair start')) }}</strong>
                </div>
                <div class="col-md-6 mb-2">
                  <span class="text-muted small d-block">{{ text('ระยะเวลาซ่อมโดยประมาณ (ETA)', 'Estimated repair time') }}</span>
                  <strong class="text-primary">{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: ซ่อมเสร็จ รอประเมิน -->
          <div v-show="(activeStep === 'done' || activeStep === 'all') && isStepReached('done')" class="sa-step-box">
            <div class="sa-step-box-header">
              <h3>
                <span class="badge badge-primary mr-1">4</span>
                {{ text('ผลการซ่อมแซม', 'Repair completion') }}
              </h3>
              <span class="text-muted small" v-if="getWorkFinishedDate()">
                {{ formatDate(getWorkFinishedDate()) }}
              </span>
            </div>

            <div class="mb-4">
              <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('บันทึกผลการซ่อมของช่าง', 'Technician repair notes') }}</h5>
              <p class="sa-description">{{ getMaintenanceDetails() || text('ยังไม่มีการบันทึกผลการซ่อม', 'No repair result recorded yet') }}</p>
            </div>

            <div class="mb-3">
              <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพถ่ายหลังซ่อมเสร็จ', 'After repair photo') }}</h5>
              <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair @open="openImage" @error="handleImageError" />
              <p v-else class="text-muted small">{{ text('ยังไม่มีภาพผลการซ่อม', 'No repair result image yet') }}</p>
            </div>

            <div v-if="report.status === 'done'" class="text-center p-3 rounded border" style="background: #eff6ff; border-color: #bfdbfe !important;">
              <span class="text-primary font-weight-bold">{{ text('ช่างแจ้งซ่อมเสร็จแล้ว อยู่ระหว่างรอนักศึกษาประเมินความพึงพอใจ', 'Repair completed, awaiting student rating') }}</span>
            </div>
          </div>

          <!-- Step 5: ประเมินแล้ว -->
          <div v-show="(activeStep === 'welldone' || activeStep === 'all') && isStepReached('welldone')" class="sa-step-box">
            <div class="sa-step-box-header">
              <h3>
                <span class="badge badge-primary mr-1">5</span>
                {{ text('ผลการประเมินความพึงพอใจ', 'Service evaluation') }}
              </h3>
              <span class="text-muted small" v-if="report.rating && report.rating.evaluatedAt">
                {{ formatDate(report.rating.evaluatedAt) }}
              </span>
            </div>

            <div v-if="report.rating && report.rating.stars">
              <!-- 1. Star Rating Banner -->
              <div class="text-center p-3 rounded bg-light border mb-4">
                <div class="sa-rating text-warning font-weight-bold mb-1" style="font-size: 1.8rem;">
                  ★ {{ report.rating.stars }} / 5
                </div>
                <p class="text-muted mb-0 small">
                  {{ text('ประเมินเมื่อ', 'Rated at') }} {{ formatDate(report.rating.evaluatedAt) }}
                </p>
                <p v-if="report.rating.comment || report.rating.feedback" class="mt-2 text-dark font-italic mb-0">
                  "{{ report.rating.comment || report.rating.feedback }}"
                </p>
              </div>

              <!-- 2. Side-by-side Image Comparison (รูปภาพก่อนซ่อม vs รูปภาพหลังซ่อม) -->
              <div class="mb-4">
                <h5 class="font-weight-bold text-dark mb-3" style="font-size: 1rem;">
                  <CIcon name="cil-image" class="mr-1 text-primary" />
                  {{ text('เปรียบเทียบรูปภาพก่อน-หลังซ่อม', 'Before & After Repair Photos') }}
                </h5>
                <div class="row">
                  <!-- Left: Before Repair Photo -->
                  <div class="col-md-6 mb-3">
                    <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                      <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                        <span>{{ text('รูปภาพก่อนซ่อม', 'Before Repair Photo') }}</span>
                      </div>
                      <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" compact @open="openImage" @error="handleImageError" />
                      <div v-else class="text-muted py-4 small">
                        <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                        {{ text('ไม่มีรูปภาพก่อนซ่อม', 'No before-repair photo') }}
                      </div>
                      <div v-if="getReportInformedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                        <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                        <span>{{ text('เวลาที่แจ้ง:', 'Reported:') }} {{ formatDate(getReportInformedDate()) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Right: After Repair Photo -->
                  <div class="col-md-6 mb-3">
                    <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                      <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                        <span>{{ text('รูปภาพหลังซ่อม', 'After Repair Photo') }}</span>

                      </div>
                      <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair compact @open="openImage" @error="handleImageError" />
                      <div v-else class="text-muted py-4 small">
                        <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                        {{ text('ไม่มีรูปภาพหลังซ่อม', 'No after-repair photo') }}
                      </div>
                      <div v-if="getWorkFinishedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                        <CIcon name="cil-check-circle" size="sm" class="mr-1 text-success" />
                        <span>{{ text('เวลาที่ซ่อมเสร็จ:', 'Completed:') }} {{ formatDate(getWorkFinishedDate()) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3. Details Grid: Technician Info & Used Spare Parts -->
              <div class="row">
                <!-- Technician Info -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 h-100 shadow-sm">
                    <div class="font-weight-bold text-dark border-bottom pb-2 mb-2">
                      <CIcon name="cil-user" class="mr-1 text-primary" />
                      {{ text('ข้อมูลช่างที่ซ่อม', 'Assigned Technician') }}
                    </div>
                    <div class="d-flex align-items-center pt-1">
                      <CIcon name="cil-wrench" class="mr-2 text-primary" size="lg" />
                      <div>
                        <strong class="text-dark d-block">
                          {{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}
                        </strong>
                        <small class="text-muted" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
                          {{ text('ซ่อมเสร็จเมื่อ', 'Completed at') }}: {{ formatDate(report.technicianPart.dateTimeFinished) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Used Spare Parts Info -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 h-100 shadow-sm">
                    <div class="font-weight-bold text-dark border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center">
                      <span>
                        <CIcon name="cil-list" class="mr-1 text-primary" />
                        {{ text('ข้อมูลอะไหล่ที่ใช้', 'Spare Parts Used') }}
                      </span>
                    </div>
                    <div v-if="hasParts()" class="pt-1">
                      <ul class="list-group list-group-flush mb-3">
                        <li v-for="(part, idx) in getTicketParts()" :key="idx" class="list-group-item bg-transparent px-0 py-2 d-flex justify-content-between align-items-center border-bottom" style="font-size: 0.95rem;">
                          <span class="text-dark font-weight-bold">
                            <span class="text-primary mr-1">{{ idx + 1 }}.</span> {{ part.name }}
                          </span>
                          <span class="badge badge-primary px-3 py-2" style="font-size: 0.88rem; font-weight: 600; border-radius: 999px;">
                            {{ part.quantity.toLocaleString() }} x {{ part.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                          </span>
                        </li>
                      </ul>
                      <div class="text-right border-top pt-2 font-weight-bold text-primary" style="font-size: 1rem;">
                        {{ $t('ticketDetail.parts.totalCost') }}: {{ getPartsTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                      </div>
                    </div>
                    <div v-else class="text-muted py-2" style="font-size: 0.95rem;">
                      <CIcon name="cil-check-circle" class="mr-1 text-success" />
                      <span>{{ isNoPartsUsed() ? ($t('ticketDetail.parts.noParts') || text('ไม่มีการใช้อะไหล่', 'No parts used')) : text('ไม่มีการบันทึกรายการอะไหล่', 'No parts recorded') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted text-center py-3">
              {{ report.status === 'done' ? text('อยู่ระหว่างรอการประเมินความพึงพอใจ', 'Awaiting rating') : text('ยังไม่มีข้อมูลการประเมิน', 'No evaluation data yet') }}
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- Modal for Image Preview -->
    <CModal :aria-label="$t('sendReport.imageLabel')" :show.sync="imageModal" centered size="lg">
      <img :src="selectedImageUrl" class="img-fluid w-100" />
      <template #footer>
        <CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') || text('ปิด', 'Close') }}</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ProblemPhotoGallery from "@/components/common/ProblemPhotoGallery.vue";
import api from '@/service/api'
import TicketDetailSummary from '@/components/common/TicketDetailSummary.vue'
import reportDetailMixin from '@/i18n/reportDetailMixin'
import {
  getTicketAssetCode,
  getTicketProblemDetails,
  getTicketProblemImage,
  getTicketMaintenanceImage,
  getTicketMaintenanceImages,
  getTicketMaintenanceDetails,
  isTicketOutdoor,
  getTicketLocationFull,
  getTicketAvailableTime,
  formatTicketDate,
  getTicketUserName,
  getTicketPersonalPhone,
  getTicketOfficePhone,
  getTicketReporterAffiliation,
  getTicketSectionName,
  getTicketJobTypeName,
  getTicketOnBehalfOf,
  getTicketTechnicianPhone,
} from '@/utils/ticketViewHelper'
import { studentTicketStatuses, studentTicketAssignees, isStudentTicketOverdue } from '@/utils/studentAffairs'

export default {
  directives: { keyboardActivate },
  name: 'StudentAffairsDetail',
  components: { CModal, ProblemPhotoGallery, TicketDetailSummary },
  mixins: [reportDetailMixin],
  props: { ticketId: { type: String, required: true } },
  data() {
    return {
      report: null,
      loading: false,
      error: '',
      updatedAt: null,
      activeStep: 'pending',
      imageModal: false,
      selectedImageUrl: null,
      activeAfterPhotoIdx: 0,
      map: null,
      marker: null,
    }
  },
  computed: {
    lang() {
      return this.$store.state.locale.lang || 'th'
    },
    backLocation() {
      const { source, ...query } = this.$route.query
      return { path: source === 'dashboard' ? '/student-affairs/dashboard' : '/student-affairs/home', query }
    },
    defaultDetailStep() {
      const status = this.report && this.report.status
      if (status === 'pending') return 'pending'
      return ['pending', 'assigned', 'in_progress', 'done', 'welldone'].includes(status)
        ? status
        : 'pending'
    },
    overdue() {
      if (this.isPostponedActive) return false;
      const isOpen = ['pending', 'assigned', 'in_progress'].includes(this.report?.status)
      return isOpen && Boolean(this.report?.slaDeadline) && new Date(this.report.slaDeadline).getTime() < Date.now()
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        }
      }
      return { lat: 0, lng: 0 }
    },
    nextStep() {
      const next = {
        pending: ['รอหน่วยงานรับเรื่องและมอบหมายช่าง', 'Awaiting assignment by the maintenance section'],
        assigned: ['รอช่างรับงานและเริ่มซ่อม', 'Awaiting technician acceptance and repair'],
        in_progress: ['ช่างกำลังดำเนินการ รอบันทึกผลการซ่อม', 'Repair is in progress; awaiting completion'],
        done: ['ช่างแจ้งซ่อมเสร็จแล้ว รอนักศึกษาประเมินบริการ', 'Technician reported completion; awaiting student rating'],
        welldone: ['มีผลการซ่อมและการประเมินบริการแล้ว', 'Repair result and service rating are available'],
        cancelled: ['สิ้นสุดการดำเนินการสำหรับรายการนี้', 'This request is no longer active'],
      }
      return (next[this.report?.status] || ['', ''])[this.lang === 'en' ? 1 : 0]
    },
    timeline() {
      const statuses = ['pending', 'assigned', 'in_progress', 'done', 'welldone']
      const dates = [
        this.getReportInformedDate(),
        this.getSupervisorInformedDate(),
        this.getWorkStartedDate(),
        this.getWorkFinishedDate(),
        this.report && this.report.rating && this.report.rating.evaluatedAt,
      ]
      const currentIdx = statuses.indexOf(this.report && this.report.status)
      return statuses.map((status, index) => {
        let reached = false
        if (index === 0 || index === 1) {
          reached = true
        } else if (this.isPostponedActive) {
          reached = false
        } else {
          reached = Boolean(dates[index]) || (currentIdx !== -1 && index <= currentIdx)
        }
        return {
          status,
          label: index === 0
            ? this.text('แจ้งเรื่อง', 'Reported')
            : (index === 1
              ? this.text('มอบหมายช่าง', 'Assign Technician')
              : this.statusLabel(status)),
          date: (this.isPostponedActive && index >= 2) ? null : dates[index],
          reached,
        }
      })
    },
  },
  watch: {
    ticketId: {
      immediate: true,
      handler() {
        this.report = null
        this.loadTicket()
      },
    },
  },
  mounted() {
    this.refreshTimer = setInterval(() => {
      if (!document.hidden && !this.loading) this.loadTicket()
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer)
    this.requestVersion = (this.requestVersion || 0) + 1
  },
  methods: {
    text(th, en) {
      return this.lang === 'en' ? en : th
    },
    statusLabel(status) {
      const map = {
        pending: ['รอรับเรื่อง', 'Pending'],
        assigned: ['มอบหมายช่าง', 'Assign Technician'],
        in_progress: ['กำลังซ่อม', 'In progress'],
        done: ['ซ่อมเสร็จ รอประเมิน', 'Repaired · awaiting rating'],
        welldone: ['ประเมินแล้ว', 'Rated'],
        cancelled: ['ยกเลิก', 'Cancelled'],
      }
      return (map[status] || [status, status])[this.lang === 'en' ? 1 : 0]
    },
    openImage(img) {
      if (!img) return
      this.selectedImageUrl = this.getImageUrl(img)
      this.imageModal = true
    },
    handleImageError(e) {
      if (e?.target) e.target.style.display = 'none'
    },
    getImageUrl(f) {
      if (!f) return ''
      const filename = f.includes('/') ? f.split('/').pop() : f
      return `${process.env.VUE_APP_APP_URL}/uploads/${encodeURIComponent(filename)}`
    },
    getUserName() {
      return getTicketUserName(this.report)
    },
    getReporterAffiliation() {
      return getTicketReporterAffiliation(this.report)
    },
    getSectionName() {
      return getTicketSectionName(this.report, this.lang)
    },
    getCategoryName() {
      return getTicketJobTypeName(this.report, this.lang)
    },
    getPersonalPhone() {
      return getTicketPersonalPhone(this.report)
    },
    getOfficePhone() {
      return getTicketOfficePhone(this.report)
    },
    getTechnicianPhone() {
      return getTicketTechnicianPhone(this.report, this.technicians || this.techniciansList, this.profile)
    },
    getLocationFull() {
      return getTicketLocationFull(this.report, this.$t.bind(this), this.$store.state.locale.lang)
    },
    getAvailableTime() {
      return getTicketAvailableTime(this.report, this.lang, this.$t.bind(this))
    },
    getOnBehalfOf() {
      return getTicketOnBehalfOf(this.report)
    },
    getAssetCode() {
      return getTicketAssetCode(this.report)
    },
    getProblemDetails() {
      return getTicketProblemDetails(this.report)
    },
    getProblemImage() {
      return getTicketProblemImage(this.report)
    },
    getMaintenanceImage() {
      return getTicketMaintenanceImage(this.report)
    },
    getMaintenanceImages() {
      return getTicketMaintenanceImages(this.report)
    },
    getActiveMaintenanceImage() {
      const images = this.getMaintenanceImages();
      if (!images || images.length === 0) return this.getMaintenanceImage();
      return images[this.activeAfterPhotoIdx] || images[0];
    },
    getMaintenanceDetails() {
      return getTicketMaintenanceDetails(this.report)
    },
    formatDate(dateString) {
      return formatTicketDate(dateString, this.lang)
    },
    isOutdoor() {
      return isTicketOutdoor(this.report)
    },
    getTechnicianName() {
      const names = studentTicketAssignees(this.report)
      if (names) return names
      const tpTech = this.report?.technicianPart?.technician
      if (tpTech && typeof tpTech === 'object') {
        const n = `${tpTech.name || ''} ${tpTech.surname || ''}`.trim()
        if (n) return n
      }
      return this.text('ยังไม่มอบหมายช่าง', 'Unassigned')
    },
    async loadTicket() {
      const version = (this.requestVersion = (this.requestVersion || 0) + 1)
      this.loading = true
      try {
        const response = await api.ticket('get', null, { urlAppend: `/student-affairs/${encodeURIComponent(this.ticketId)}` })
        if (version !== this.requestVersion) return
        this.report = response.data.data
        this.updatedAt = Date.now()
        this.error = ''
        if (this.defaultDetailStep) {
          this.activeStep = this.defaultDetailStep
        }
      } catch (error) {
        if (version !== this.requestVersion) return
        this.report = null
        this.error = this.text('ไม่สามารถเปิดงานนี้ได้ งานอาจไม่อยู่ในขอบเขตนักศึกษาหรือโหลดข้อมูลไม่สำเร็จ', 'Unable to open this request. It may be outside the student scope, or the request failed.')
      } finally {
        if (version === this.requestVersion) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.sa-detail {
  color: #0f172a;
  max-width: 1300px;
  margin: 0 auto;
}
.sa-detail-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}
.sa-detail-back,
.sa-refresh-action {
  font-weight: 700;
  border-radius: 8px;
  padding: 7px 16px;
  display: inline-flex;
  align-items: center;
}
.star.filled {
  color: #f59e0b !important;
}
::v-deep .ticket-detail-summary .summary-ticket {
  display: none !important;
}
::v-deep .ticket-detail-summary .summary-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e3a8a;
}
</style>
